import React from "react";
import { downloadWorkbook } from "../../../Functionalities/exportWorkbook";

const AVATAR_PALETTES = [
  { bg: "#ecfdf5", text: "#047857" }, // emerald
  { bg: "#eef2ff", text: "#4338ca" }, // indigo
  { bg: "#fff7ed", text: "#c2410c" }, // orange
  { bg: "#faf5ff", text: "#7e22ce" }, // purple
  { bg: "#fffbeb", text: "#b45309" }, // amber
  { bg: "#f1f5f9", text: "#334155" }, // slate
];

const getInitials = (name) => {
  if (!name) return "--";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return String(name).slice(0, 2).toUpperCase();
};

const getPalette = (index) => AVATAR_PALETTES[index % AVATAR_PALETTES.length];

export default function TopListBlock({
  title,
  subtitle,
  rows,
  columns,
  note,
  onShowAll,
  exportName,
  badgeText,
  badgeType = "neutral",
  isStoreTable = false,
  showQtyBar = false,
  actionButtonLabel,
  onActionClick,
}) {
  const exportRows = () =>
    downloadWorkbook({
      columns: columns.map((c) => ({ key: c.key, label: c.label })),
      rows: (rows || []).map((row) => Object.fromEntries(columns.map((c) => [c.key, row[c.key]]))),
      sheetName: title.slice(0, 30),
      fileName: exportName,
    });

  const rowKey = (row, index) => row.branch_id || row.item_id || row.user_id || row.promocode_id || index;
  const maxQty = showQtyBar && rows && rows.length > 0 ? Math.max(1, ...rows.map((r) => r.qty || 0)) : 1;

  return (
    <div className="ins-card" style={{ display: "flex", flexDirection: "column" }}>
      <div className="ins-card-header">
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <h3 className="ins-card-title">{title}</h3>
            {badgeText && (
              <span
                style={{
                  padding: "0.15rem 0.5rem",
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  borderRadius: "0.25rem",
                  backgroundColor: badgeType === "rose" ? "#fff1f2" : "#f1f5f9",
                  color: badgeType === "rose" ? "#be123c" : "#475569",
                  border: badgeType === "rose" ? "1px solid #fecdd3" : "1px solid #e2e8f0",
                }}
              >
                {badgeText}
              </span>
            )}
          </div>
          {(subtitle || note) && <p className="ins-card-subtitle">{subtitle || note}</p>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {onShowAll && rows && rows.length >= 10 && (
            <button
              type="button"
              className="ins-btn-action"
              style={{ height: "30px", padding: "0 0.625rem", fontSize: "0.75rem" }}
              onClick={onShowAll}
            >
              Show all
            </button>
          )}
          <button
            type="button"
            className="ins-btn-action"
            style={{ height: "30px", padding: "0 0.625rem", fontSize: "0.75rem" }}
            onClick={exportRows}
            disabled={!rows || rows.length === 0}
            title="Download Excel Spreadsheet"
          >
            <span className="material-symbols-outlined" style={{ fontSize: "15px", color: "#059669" }}>file_download</span>
            <span>Excel</span>
          </button>
        </div>
      </div>

      <div className="ins-card-body" style={{ padding: 0 }}>
        {!rows ? (
          <div style={{ padding: "2rem", textAlign: "center", color: "#94a3b8", fontSize: "0.8125rem" }}>
            Loading records…
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: "2rem", textAlign: "center", color: "#94a3b8", fontSize: "0.8125rem" }}>
            No records in this date range.
          </div>
        ) : (
          <div className="ins-table-wrap custom-scroll" style={{ maxHeight: "560px", overflowY: "auto" }}>
            <table className="ins-modern-table">
              <thead>
                <tr>
                  <th style={{ width: "40px", textAlign: "center" }}>#</th>
                  {columns.map((c) => (
                    <th
                      key={c.key}
                      className={`${c.numeric ? "num" : ""} ${c.center ? "center" : ""}`}
                    >
                      {c.label}
                    </th>
                  ))}
                  {actionButtonLabel && <th style={{ textAlign: "right", paddingRight: "1rem" }}>Action</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const palette = getPalette(index);
                  return (
                    <tr key={rowKey(row, index)}>
                      <td style={{ textAlign: "center", fontWeight: 700, color: "#94a3b8", fontSize: "13px" }}>
                        {index + 1}
                      </td>

                      {columns.map((c, colIdx) => {
                        const val = row[c.key];
                        const formatted = c.format ? c.format(val, row) : val;

                        // First column with store avatar
                        if (colIdx === 0 && isStoreTable) {
                          return (
                            <td key={c.key}>
                              <div style={{ display: "flex", alignItems: "center" }}>
                                <span
                                  className="ins-avatar-badge"
                                  style={{ backgroundColor: palette.bg, color: palette.text }}
                                >
                                  {getInitials(row[c.key])}
                                </span>
                                <div>
                                  <div style={{ fontWeight: 600, color: "#0f172a", fontSize: "14px" }}>{formatted}</div>
                                  {c.sub && <div style={{ fontSize: "12px", color: "#94a3b8" }}>{c.sub(row)}</div>}
                                </div>
                              </div>
                            </td>
                          );
                        }

                        // Qty column with mini progress bar
                        if (c.key === "qty" && showQtyBar) {
                          const pctWidth = Math.max(10, Math.min(100, Math.round((Number(val || 0) / maxQty) * 100)));
                          return (
                            <td key={c.key} style={{ textAlign: "center" }}>
                              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "14px" }}>{formatted}</div>
                              <div style={{ width: "48px", height: "4px", backgroundColor: "#f1f5f9", borderRadius: "9999px", margin: "4px auto 0", overflow: "hidden" }}>
                                <div style={{ width: `${pctWidth}%`, height: "100%", backgroundColor: "#fb310d", borderRadius: "9999px" }} />
                              </div>
                            </td>
                          );
                        }

                        // Rating badge
                        if (c.key === "rating" || c.key === "avg") {
                          return (
                            <td key={c.key} style={{ textAlign: "center" }}>
                              <span className="ins-rating-pill">
                                {formatted}
                              </span>
                            </td>
                          );
                        }

                        // Cancellation rate badge
                        if (c.key === "cancellation_rate" || c.key === "rate") {
                          const numVal = Number(val || 0);
                          const pillClass = numVal === 0 ? "green" : numVal <= 15 ? "amber" : "rose";
                          return (
                            <td key={c.key} style={{ textAlign: "center" }}>
                              <span className={`ins-pill-badge ${pillClass}`}>
                                {formatted}
                              </span>
                            </td>
                          );
                        }

                        return (
                          <td
                            key={c.key}
                            className={`${c.numeric ? "num" : ""} ${c.center ? "center" : ""}`}
                            style={{ fontSize: "14px", ...(c.bold ? { fontWeight: 700, color: "#0f172a" } : {}) }}
                          >
                            <div style={{ fontSize: "14px" }}>{formatted}</div>
                            {c.sub && <span style={{ display: "block", fontSize: "12px", color: "#94a3b8" }}>{c.sub(row)}</span>}
                          </td>
                        );
                      })}

                      {actionButtonLabel && (
                        <td style={{ textAlign: "right", paddingRight: "1rem" }}>
                          <button
                            type="button"
                            onClick={() => onActionClick && onActionClick(row)}
                            style={{
                              border: "none",
                              background: "transparent",
                              color: "#4648d4",
                              fontWeight: 600,
                              fontSize: "12px",
                              cursor: "pointer",
                              padding: "0.25rem 0.5rem",
                              borderRadius: "0.25rem",
                              transition: "background-color 0.15s ease",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(70, 72, 212, 0.08)")}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                          >
                            {actionButtonLabel}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
